import { useState } from "react";

import type { EditorImage } from "../editor/types";
import { MAX_ZOOM, MIN_ZOOM } from "./canvas/useViewport";
import { Icon } from "./Icon";

interface FilmstripProps {
  images: EditorImage[];
  activeIndex: number;
  zoom: number;
  showOutlines: boolean;
  showGrid: boolean;
  comparing: boolean;
  onSelect: (index: number) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onReorder: (from: number, to: number) => void;
  onZoomChange: (zoom: number) => void;
  onFit: () => void;
  onShowOutlinesChange: (value: boolean) => void;
  onShowGridChange: (value: boolean) => void;
  onCompareChange: (comparing: boolean) => void;
}

const ZOOM_STEP = 1.25;

export function Filmstrip({
  images,
  activeIndex,
  zoom,
  showOutlines,
  showGrid,
  comparing,
  onSelect,
  onAdd,
  onRemove,
  onReorder,
  onZoomChange,
  onFit,
  onShowOutlinesChange,
  onShowGridChange,
  onCompareChange,
}: FilmstripProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const endDrag = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  return (
    <footer className="filmstrip">
      <div className="thumbnail-scroller" aria-label="画像一覧">
        {images.length > 1 &&
          images.map((image, index) => (
            <div
              key={image.id}
              className={`thumbnail-item ${dragOverIndex === index ? "is-drag-over" : ""}`}
              draggable
              // The app shell listens for file drops, so reorder drags must
              // never bubble up and raise the "drop images" overlay.
              onDragStart={(event) => {
                event.stopPropagation();
                event.dataTransfer.effectAllowed = "move";
                setDragIndex(index);
              }}
              onDragEnter={(event) => event.stopPropagation()}
              onDragOver={(event) => {
                if (dragIndex === null) return;
                event.preventDefault();
                event.stopPropagation();
                setDragOverIndex(index);
              }}
              onDragLeave={(event) => event.stopPropagation()}
              onDrop={(event) => {
                if (dragIndex === null) return;
                event.preventDefault();
                event.stopPropagation();
                if (dragIndex !== index) onReorder(dragIndex, index);
                endDrag();
              }}
              onDragEnd={(event) => {
                event.stopPropagation();
                endDrag();
              }}
            >
              <button
                className={`thumbnail-button ${index === activeIndex ? "is-active" : ""}`}
                type="button"
                aria-label={`${index + 1}: ${image.name}`}
                aria-current={index === activeIndex ? "true" : undefined}
                title={image.name}
                onClick={() => onSelect(index)}
              >
                <img src={image.thumbnailUrl} alt="" draggable={false} />
                {image.dirty && <span className="dirty-dot" title="未ダウンロードの変更" />}
                {image.saved && !image.dirty && (
                  <span className="saved-badge" title="ダウンロード済み">
                    <Icon name="check" size={10} />
                  </span>
                )}
              </button>
              <button
                className="thumbnail-remove"
                type="button"
                aria-label={`${image.name} を一覧から外す`}
                title="一覧から外す"
                onClick={() => onRemove(index)}
              >
                <Icon name="close" size={11} />
              </button>
            </div>
          ))}
        <button
          className={`thumbnail-add ${images.length > 1 ? "" : "is-wide"}`}
          type="button"
          aria-label="画像を追加"
          title="画像を追加"
          onClick={onAdd}
        >
          <Icon name="plus" size={18} />
          {images.length <= 1 && <span>画像を追加</span>}
        </button>
        {images.length > 1 && (
          <span className="image-counter">
            {activeIndex + 1} / {images.length}
          </span>
        )}
      </div>

      <div className="view-controls">
        <button
          className={`icon-button ${comparing ? "is-active" : ""}`}
          type="button"
          aria-label="押している間、元の画像を表示"
          aria-pressed={comparing}
          title="押している間、元の画像を表示 (C)"
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            onCompareChange(true);
          }}
          onPointerUp={() => onCompareChange(false)}
          onPointerCancel={() => onCompareChange(false)}
          onKeyDown={(event) => {
            if (event.key === " " || event.key === "Enter") onCompareChange(true);
          }}
          onKeyUp={() => onCompareChange(false)}
          onBlur={() => onCompareChange(false)}
        >
          <Icon name="compare" size={18} />
        </button>
        <button
          className={`icon-button ${showOutlines ? "is-active" : ""}`}
          type="button"
          aria-label="範囲の枠線を表示"
          aria-pressed={showOutlines}
          title="範囲の枠線を表示"
          onClick={() => onShowOutlinesChange(!showOutlines)}
        >
          <Icon name="outline" size={18} />
        </button>
        <button
          className={`icon-button ${showGrid ? "is-active" : ""}`}
          type="button"
          aria-label="モザイクの格子を表示"
          aria-pressed={showGrid}
          title="モザイクの格子を表示"
          onClick={() => onShowGridChange(!showGrid)}
        >
          <Icon name="grid" size={17} />
        </button>
        <span className="view-divider" aria-hidden="true" />
        <button
          className="icon-button"
          type="button"
          aria-label="縮小"
          title="縮小 (Ctrl/Cmd+-)"
          disabled={zoom <= MIN_ZOOM}
          onClick={() => onZoomChange(Math.max(MIN_ZOOM, zoom / ZOOM_STEP))}
        >
          <Icon name="minus" size={16} />
        </button>
        <input
          className="zoom-slider"
          type="range"
          min={Math.log(MIN_ZOOM)}
          max={Math.log(MAX_ZOOM)}
          step={0.01}
          value={Math.log(zoom)}
          aria-label="ズーム"
          aria-valuetext={`${Math.round(zoom * 100)}%`}
          onChange={(event) => onZoomChange(Math.exp(Number(event.target.value)))}
        />
        <button
          className="icon-button"
          type="button"
          aria-label="拡大"
          title="拡大 (Ctrl/Cmd++)"
          disabled={zoom >= MAX_ZOOM}
          onClick={() => onZoomChange(Math.min(MAX_ZOOM, zoom * ZOOM_STEP))}
        >
          <Icon name="plus" size={16} />
        </button>
        <button
          className="zoom-label"
          type="button"
          aria-label="画面に合わせる"
          title="画面に合わせる (Ctrl/Cmd+0)"
          onClick={onFit}
        >
          {Math.round(zoom * 100)}%
        </button>
      </div>
    </footer>
  );
}
